import { CircularProgress, DialogTitle } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { fetchTimeOffRequestEventsByTorId } from "../api/timeOffRequest.js";
import { selectUserSession } from "../slices/userSession/userSessionSlice.js";
import {
  format_DOW_ShortMonth_Date_Year,
  format_LongMonth_Date_Year_Time,
  getShiftTime,
} from "../util/dateTimeFormatter.js";
import { getScrubbedTimeOffRequestEvents } from "../util/timeOffRequestScrubber.js";
import SlideUpTransition from "./fundamentals/SlideUpTransition";
import StarsCloseIconButton from "./fundamentals/StarsCloseIconButton.jsx";
import { isLWOPTOWOPDayOfRequest, isLWOPTOWOPRequest } from "../util/timeOffRequestUtils.js";
import { getTORApprovedLeaveTime, getTORPreferredLeaveTime } from "../util/timeOffExportMapper.js";

const TimeOffRequestDialogDesktop = (props) => {
  const userSession = useSelector(selectUserSession);
  const [timeOffRequestEventList, setTimeOffRequestEventList] = useState([]);
  const [isTimeOffRequestEventListLoading, setIsTimeOffRequestEventListLoading] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  useEffect(() => {
    setIsFirstLoad(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFirstLoad) {
      fetchTimeOffRequestEventsByTorId(props.timeOffRequest.torId, userSession.eid, props.isAdminPage)
        .then((res) => res.json())
        .then((result) => {
          let scrubbedTimeOffRequestEvents = getScrubbedTimeOffRequestEvents(result.timeOffRequestEvents);
          setTimeOffRequestEventList(scrubbedTimeOffRequestEvents);
          setIsTimeOffRequestEventListLoading(false);
          setIsFirstLoad(false);
        })
        .catch((err) => {
          console.error("error happened!", err);
          setIsTimeOffRequestEventListLoading(false);
          setIsFirstLoad(false);
        });
    }
  }, [props.timeOffRequest, props.isAdminPage, isFirstLoad, userSession.eid]);

  const handleClickDialogClose = (event, reason) => {
    if (reason && reason === "backdropClick") return;
    props.onClickDialogClose(event, reason);
  };

  const renderEventStatusHistory = () => {
    if (timeOffRequestEventList) {
      return timeOffRequestEventList.map((timeOffRequestEvent) => {
        if (!timeOffRequestEvent.flags.isAdditionalApproved) {
          return (
            <Paper className="status-history-event" key={timeOffRequestEvent.eventId}>
              <Grid item xs={12}>
                <div className="bold-text">
                  {timeOffRequestEvent.modifiedByEid === ""
                    ? timeOffRequestEvent.timeOffRequestStatus
                    : timeOffRequestEvent.timeOffRequestStatus +
                      " - " +
                      timeOffRequestEvent.modifiedByLastName +
                      ", " +
                      timeOffRequestEvent.modifiedByFirstName}
                </div>
                {timeOffRequestEvent.cannedReason ? <div>Reason: {timeOffRequestEvent.cannedReason}</div> : ""}
                {timeOffRequestEvent.reasonComments ? <div>Comments: {timeOffRequestEvent.reasonComments}</div> : ""}
                <div>{format_LongMonth_Date_Year_Time(timeOffRequestEvent.createdAt)}</div>
              </Grid>
            </Paper>
          );
        }
        return "";
      });
    }
  };

  return (
    <Dialog
      className="detail-dialog"
      data-testid="time-off-request-detail-dialog"
      fullWidth
      maxWidth="md"
      open={props.isDialogOpen}
      TransitionComponent={SlideUpTransition}
      keepMounted
      onClose={handleClickDialogClose}
      aria-labelledby="alert-dialog-slide-title"
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogTitle>
        <StarsCloseIconButton onClick={handleClickDialogClose} />
      </DialogTitle>
      <DialogContent className="detail-dialog-content">
        <DialogContentText component={"div"} id="alert-dialog-slide-description">
          <Grid container>
            <Grid item xs={12} sm={6}>
              <Paper className="detail-dialog-section">
                <Typography component={"div"}>
                  <Box className="detail-dialog-section-title">Time Off Information</Box>
                </Typography>
                <Grid container>
                  <Grid item xs={6}>
                    <div>Reference #</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.referenceNumber}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Type</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.timeOffRequestType}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Latest Status</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.visibleStatus}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Station</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.station}</div>
                  </Grid>
                </Grid>
              </Paper>
              <Paper className="detail-dialog-section">
                <Typography component={"div"}>
                  <Box className="detail-dialog-sub-section-title">Employee</Box>
                </Typography>
                <Grid container>
                  <Grid item xs={6}>
                    <div>Name</div>
                  </Grid>
                  <Grid item xs={6}>
                    {" "}
                    <div className="detail-dialog-right-text">
                      {props.timeOffRequest.empLastName}, {props.timeOffRequest.empFirstName}
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>ID</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.empId}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Workgroup</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">{props.timeOffRequest.empWorkgroup}</div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Shift Date</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">
                      {format_DOW_ShortMonth_Date_Year(props.timeOffRequest.shiftStart)}
                    </div>
                  </Grid>
                  <Grid item xs={12}>
                    <Divider />
                  </Grid>
                  <Grid item xs={6}>
                    <div>Shift Time</div>
                  </Grid>
                  <Grid item xs={6}>
                    <div className="detail-dialog-right-text">
                      {getShiftTime(props.timeOffRequest.shiftStart) +
                        "-" +
                        getShiftTime(props.timeOffRequest.shiftEnd)}
                    </div>
                  </Grid>
                  {isLWOPTOWOPDayOfRequest(props.timeOffRequest.timeOffRequestType) && (
                    <>
                      <Grid item xs={12}>
                        <Divider />
                      </Grid>
                      <Grid item xs={6}>
                        <div>Preferred Leave Time</div>
                      </Grid>
                      <Grid item xs={6}>
                        <div className="detail-dialog-right-text">{getTORPreferredLeaveTime(props.timeOffRequest)}</div>
                      </Grid>
                    </>
                  )}
                  {isLWOPTOWOPRequest(props.timeOffRequest.timeOffRequestType) &&
                    props.timeOffRequest.approvedWOPEnd &&
                    props.timeOffRequest.approvedWOPStart && (
                      <>
                        <Grid item xs={12}>
                          <Divider />
                        </Grid>
                        <Grid item xs={6}>
                          <div>Approved Leave Time</div>
                        </Grid>
                        <Grid item xs={6}>
                          <div className="detail-dialog-right-text">
                            {getTORApprovedLeaveTime(props.timeOffRequest)}
                          </div>
                        </Grid>
                      </>
                    )}
                </Grid>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Paper className="detail-dialog-section">
                <Typography component={"div"}>
                  <Box className="detail-dialog-section-title">Status History</Box>
                </Typography>
                {isTimeOffRequestEventListLoading ? (
                  <CircularProgress />
                ) : (
                  <Grid container>{renderEventStatusHistory()}</Grid>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContentText>
      </DialogContent>
    </Dialog>
  );
};

TimeOffRequestDialogDesktop.propTypes = {
  timeOffRequest: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  isAdminPage: PropTypes.bool,
  isDialogOpen: PropTypes.bool.isRequired,
  onClickDialogOpen: PropTypes.func.isRequired,
  onClickDialogClose: PropTypes.func.isRequired,
};

export default TimeOffRequestDialogDesktop;
